/**
 * Frontend auth mode. Controls how the API client authenticates against
 * the read API. Set via VITE_AUTH_MODE at build time:
 *
 *   - bearer (default): legacy. Sends `Authorization: Bearer
 *     ${VITE_READ_API_BEARER}` on every request. No login screen.
 *   - cookie: never sends Authorization. All requests use
 *     `credentials: 'include'` so the browser sends the signed session
 *     cookie set by POST /auth/login. Shows a login screen when
 *     /auth/me returns 401.
 *   - hybrid: sends BOTH the bearer header (if VITE_READ_API_BEARER is
 *     set) AND credentials. The server's requireReadAuth prefers the
 *     bearer when present; the cookie is the fallback. Intended for
 *     local dev during the cutover — once the cookie flow is verified
 *     end-to-end, drop the bearer env var and switch to `cookie`.
 *
 * The default is 'bearer' so existing setups keep working unchanged.
 */
export type AuthMode = 'bearer' | 'cookie' | 'hybrid';

const VALID_MODES: AuthMode[] = ['bearer', 'cookie', 'hybrid'];

function parseAuthMode(raw: unknown): AuthMode {
  if (typeof raw === 'string' && (VALID_MODES as string[]).includes(raw)) {
    return raw as AuthMode;
  }
  return 'bearer';
}

export const AUTH_MODE: AuthMode = parseAuthMode(import.meta.env.VITE_AUTH_MODE);

export const BEARER: string = import.meta.env.VITE_READ_API_BEARER ?? '';

/** True when /auth/me, login, logout are valid client-side calls. */
export function isCookieAuthEnabled(mode: AuthMode = AUTH_MODE): boolean {
  return mode === 'cookie' || mode === 'hybrid';
}

/**
 * True when the API client should attach `Authorization: Bearer ${BEARER}`.
 *
 * `bearer` mode: always true (legacy). An empty BEARER produces
 * `Bearer ` which the API rejects — same misconfigured-misbehaving
 * shape as before the auth-mode work, so existing tests stay accurate.
 *
 * `hybrid` mode: only true when a bearer is actually configured. This
 * lets `hybrid` with VITE_READ_API_BEARER='' behave like `cookie` so the
 * dashboard can be cut over by clearing the bearer env var without
 * rebuilding.
 *
 * `cookie` mode: always false.
 */
export function isBearerAuthEnabled(mode: AuthMode = AUTH_MODE): boolean {
  if (mode === 'bearer') return true;
  if (mode === 'hybrid') return BEARER.length > 0;
  return false;
}

/**
 * Test seam: tests inject a mode by importing this from a test file. Not
 * used in production; production reads import.meta.env directly via
 * AUTH_MODE above.
 */
export const __test = { parseAuthMode };
